import type { CommunityEntry, MyCommunity, PublicFit } from "@cfg/api-contract";
import { useDesign } from "@cfg/configurator-ui";
import type { ParamSpec } from "@cfg/design-sdk";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { COMMUNITY_SORTS, filterCommunity, sortCommunity, type CommunitySort } from "../community.ts";
import { paramSummary, readableSummary, type SummaryUi } from "../config-name.ts";
import { formatDownloads } from "../downloads.ts";
import { errorCopyKey } from "../errors.ts";
import { useFitInfo } from "../fits.ts";

export function communityQueryKey(brand: string, designId: string) {
  return ["design-community", brand, designId] as const;
}

/** Community entries of this design and its family, as the API lists them. */
export function useDesignCommunity(designId: string) {
  const { api, brand } = useApp();
  return useQuery({ queryKey: communityQueryKey(brand.config.id, designId), queryFn: () => api!.designCommunity(designId), enabled: !!api });
}

export function likesQueryKey(brand: string, sub: string | undefined) {
  return ["likes", brand, sub] as const;
}

/** Entries the signed-in user marked as favorites; empty when signed out. */
export function useMyLikes() {
  const { api, auth, brand } = useApp();
  const user = useAuthUser(auth);
  return useQuery({ queryKey: likesQueryKey(brand.config.id, user?.sub), queryFn: async () => (await api!.myLikes()).items, enabled: !!(api && user) });
}

export function myCommunityQueryKey(brand: string, sub: string | undefined) {
  return ["my-community", brand, sub] as const;
}

/** The signed-in user's tags and prints (`${entryId}#${ref}`) and the entries they tagged; undefined when signed out. */
export function useMyCommunity() {
  const { api, auth, brand } = useApp();
  const user = useAuthUser(auth);
  return useQuery({ queryKey: myCommunityQueryKey(brand.config.id, user?.sub), queryFn: () => api!.myCommunity(), enabled: !!(api && user) });
}

/**
 * Keep cached community data in step with an answer from the API: the design's
 * list, the user's favorites and their own tags and prints.
 */
export function useCommunityCache(designId: string) {
  const { brand, auth } = useApp();
  const user = useAuthUser(auth);
  const qc = useQueryClient();
  const listKey = communityQueryKey(brand.config.id, designId);
  const mineKey = myCommunityQueryKey(brand.config.id, user?.sub);
  type List = { items: CommunityEntry[]; designs: string[] } | undefined;
  const swap = (items: CommunityEntry[], entry: CommunityEntry) => items.map((i) => (i.id === entry.id ? entry : i));
  return {
    /** Put the entry first in the list (new or just tagged), or update it where it is. */
    upsert(entry: CommunityEntry, { front = true } = {}) {
      qc.setQueryData(listKey, (old: List) => old && { ...old, items: front ? [entry, ...old.items.filter((i) => i.id !== entry.id)] : swap(old.items, entry) });
      qc.setQueryData(likesQueryKey(brand.config.id, user?.sub), (old: CommunityEntry[] | undefined) => old && swap(old, entry));
      qc.setQueryData(mineKey, (old: MyCommunity | undefined) => old && { ...old, items: swap(old.items, entry) });
    },
    /** The entry is gone (its last tag was removed). */
    drop(id: string) {
      qc.setQueryData(listKey, (old: List) => old && { ...old, items: old.items.filter((i) => i.id !== id) });
      qc.setQueryData(likesQueryKey(brand.config.id, user?.sub), (old: CommunityEntry[] | undefined) => old?.filter((i) => i.id !== id));
      qc.setQueryData(mineKey, (old: MyCommunity | undefined) => old && { ...old, items: old.items.filter((i) => i.id !== id), tags: old.tags.filter((t) => !t.startsWith(`${id}#`)) });
    },
    /** Note refs the user now owns (`tags`) and registered prints for (`prints`) on an entry. */
    markMine(id: string, tags: string[], prints: string[]) {
      qc.setQueryData(mineKey, (old: MyCommunity | undefined) =>
        old && { ...old, tags: [...new Set([...old.tags, ...tags.map((r) => `${id}#${r}`)])], prints: [...new Set([...old.prints, ...prints.map((r) => `${id}#${r}`)])] },
      );
      if (tags.length) void qc.invalidateQueries({ queryKey: mineKey });
    },
    /** A tag of the user's is gone. */
    unmarkTag(id: string, ref: string) {
      qc.setQueryData(mineKey, (old: MyCommunity | undefined) => old && { ...old, tags: old.tags.filter((t) => t !== `${id}#${ref}`), prints: old.prints.filter((t) => t !== `${id}#${ref}`) });
    },
  };
}

/** "3 prints" for one fit, or "No prints yet". */
export function useFitPrintsText() {
  const { copy, locale } = useApp();
  return (n: number) => (n === 0 ? copy("community.fitUnconfirmed") : n === 1 ? copy("community.fitPrints.one") : copy("community.fitPrints.other", { count: n.toLocaleString(locale) }));
}

/** A fit tag: green with ✓ and its print count once someone printed it for that fit, dashed until then. */
export function FitPill({ fit }: { fit: Pick<PublicFit, "ref" | "name" | "owner" | "confirmCount"> }) {
  const { copy } = useApp();
  const printsText = useFitPrintsText();
  const prints = fit.confirmCount;
  const status = printsText(prints);
  const title = [status, copy("community.taggedBy", { name: fit.owner }), fit.name].filter(Boolean).join(" · ");
  return (
    <span className={`cfg-fit-chip community-fit ${prints > 0 ? "is-confirmed" : "is-unconfirmed"}`} title={title}>
      {prints > 0 && (
        <span className="community-fit-count" aria-hidden="true">
          ✓ {prints}
        </span>
      )}
      <strong>{fit.ref}</strong>
      {fit.name && <span>{fit.name}</span>}
      <span className="cfg-visually-hidden"> ({status})</span>
    </span>
  );
}

/** An entry's name: its dimensions as people read them ("Wheel diameter 50 mm · …"), in the design open on the model. */
export function useEntryName() {
  const { description, spec } = useDesign();
  const defaults = description?.defaults;
  const ui = description?.ui as SummaryUi | undefined;
  return useCallback(
    (e: CommunityEntry) => (defaults ? readableSummary(spec as ParamSpec[], e.params, defaults, ui) || paramSummary(spec as ParamSpec[], e.params, defaults) : e.fits.map((f) => f.ref).join(" ")),
    [spec, defaults, ui],
  );
}

/** An entry's parameter code ("WD50 WT12 WS4 HA45 M"), the way the part is engraved: tooltip and search. */
export function useEntryCode() {
  const { description, spec } = useDesign();
  const defaults = description?.defaults;
  return useCallback((e: CommunityEntry) => (defaults ? paramSummary(spec as ParamSpec[], e.params, defaults) : ""), [spec, defaults]);
}

export interface CommunityPanelProps {
  designId: string;
  /** Display title per design id (catalog), for entries made for a family sibling. */
  titleOf: (designId: string) => string;
  /** Mount name of a design in a family ("Wall", "SKÅDIS"), shown as a badge on every entry; undefined outside families. */
  variantOf?: (designId: string) => string | undefined;
  /** The entry whose params are on the model right now, if any. */
  onModelId: string | null;
  /** Put an entry's params on the model. */
  onApply: (entry: CommunityEntry) => Promise<void>;
  /** Entry to put on the model once the list has loaded (`?community=<id>`). */
  initialId?: string;
  /** Set to list only the entries tagged for (`?fit=<ref>`, from the home page); the user can clear it. */
  initialFit?: string;
  /** Shown above the list (the entry bar on phones). */
  header?: React.ReactNode;
}

/**
 * The community's brackets for this design and its family: parameter sets
 * nobody owns, listed by their parameters, with how many sets they're tagged
 * for and how many people printed them. Clicking one puts it on the model;
 * its sets, favorite, link and reports are in the entry bar (`CommunityEntryBar`).
 */
export function CommunityPanel({ designId, titleOf, variantOf, onModelId, onApply, initialId, initialFit, header }: CommunityPanelProps) {
  const { brand, copy, locale } = useApp();
  const ids = useId();
  const { description } = useDesign();
  const q = useDesignCommunity(designId);
  const likes = useMyLikes();
  const nameOf = useEntryName();
  const codeOf = useEntryCode();
  const searchName = useCallback((e: CommunityEntry) => `${nameOf(e)} ${codeOf(e)}`, [nameOf, codeOf]);
  const fitConfig = brand.config.fitTags;
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<CommunitySort>("newest");
  const [fit, setFit] = useState(initialFit);
  const fitName = fit ? q.data?.items.flatMap((e) => e.fits).find((f) => f.ref === fit && f.name)?.name : undefined;
  const fitInfo = useFitInfo(fit, fitName);
  const items = useMemo(
    () => sortCommunity(filterCommunity((q.data?.items ?? []).filter((e) => !fit || e.fits.some((f) => f.ref === fit)), query, { titleOf, nameOf: searchName }), sort, locale, nameOf),
    [q.data, fit, query, sort, titleOf, nameOf, searchName, locale],
  );
  const total = q.data?.items.length ?? 0;
  const searched = !!query.trim() || !!fit;
  const likedIds = new Set((likes.data ?? []).map((e) => e.id));

  // `?community=<id>`: put that entry on the model, once the list is in.
  const appliedInitial = useRef(false);
  useEffect(() => {
    if (appliedInitial.current || !initialId || !description || !q.data) return;
    appliedInitial.current = true;
    const e = q.data.items.find((i) => i.id === initialId);
    if (e) {
      void onApply(e);
      requestAnimationFrame(() => document.getElementById(`${ids}-${e.id}`)?.scrollIntoView({ block: "nearest" }));
    }
  }, [initialId, description, q.data]);

  if (q.isLoading) return <p className="muted small community-status">{copy("common.loading")}</p>;
  if (q.isError)
    return (
      <div className="community-status community-error" role="alert">
        <p className="cfg-error">{copy("community.error")}</p>
        <p className="muted small">{copy(errorCopyKey(q.error))}</p>
        <button type="button" className="cfg-btn" onClick={() => void q.refetch()}>
          {copy("common.retry")}
        </button>
      </div>
    );

  return (
    <div className="community-panel">
      {header}
      {fitInfo && (
        <div className="community-fit-filter" role="status">
          {fitInfo.imageUrl && <img className="community-fit-filter-img" src={fitInfo.imageUrl} alt="" onError={(ev) => (ev.currentTarget.hidden = true)} />}
          <span className="community-fit-filter-text">
            <small>{copy("community.fitFilter")}</small>
            <strong>{fitInfo.ref}</strong>
            {fitInfo.name && <span>{fitInfo.name}</span>}
          </span>
          <button type="button" className="cfg-chip community-fit-filter-clear" onClick={() => setFit(undefined)}>
            {copy("community.fitFilterClear")}
          </button>
        </div>
      )}
      {total > 0 && (
        <div className="community-tools" role="search">
          <input
            id={`${ids}-q`}
            className="cfg-input community-search"
            type="search"
            enterKeyHint="search"
            placeholder={copy("community.searchLabel")}
            aria-label={copy("community.searchLabel")}
            value={query}
            onChange={(ev) => setQuery(ev.target.value)}
          />
          <label className="community-sort">
            <span className="cfg-visually-hidden">{copy("community.sortLabel")}</span>
            <select className="cfg-input" value={sort} onChange={(ev) => setSort(ev.target.value as CommunitySort)} aria-label={copy("community.sortLabel")}>
              {COMMUNITY_SORTS.map((s) => (
                <option key={s} value={s}>
                  {copy(`community.sort.${s}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {total > 0 && (
        <p className="muted small community-summary" aria-live="polite">
          {!searched && total === 1 ? copy("community.countOne") : copy("community.count", { count: searched ? `${items.length}/${total}` : total })}
        </p>
      )}

      {total === 0 ? (
        <div className="community-empty">
          <p>{copy("community.empty")}</p>
          <p className="muted small">{copy("community.emptyHint")}</p>
        </div>
      ) : items.length === 0 ? (
        <p className="muted community-empty">{fit && !query.trim() ? copy("community.fitNone", { ref: fit }) : copy("community.noResults", { query: query.trim() || (fit ?? "") })}</p>
      ) : (
        <ul className="community-list" aria-label={copy("community.title")}>
          {items.map((e) => {
            const isOnModel = onModelId === e.id;
            const sibling = e.designId !== designId;
            const mount = variantOf?.(e.designId);
            const sets = e.fits.length;
            const prints = e.printerCount;
            return (
              <li key={e.id} id={`${ids}-${e.id}`} className={`community-entry${isOnModel ? " is-applied" : ""}`}>
                <button type="button" className="community-entry-main" aria-pressed={isOnModel} onClick={() => !isOnModel && void onApply(e)}>
                  <span className="community-entry-head">
                    <strong className="community-entry-name" title={copy("mine.codeHint", { code: codeOf(e) })}>
                      {nameOf(e)}
                    </strong>
                    {mount && (
                      <span className={`community-entry-mount${sibling ? " is-other" : ""}`} title={sibling ? copy("community.madeFor", { design: titleOf(e.designId) }) : undefined}>
                        {mount}
                      </span>
                    )}
                    {likedIds.has(e.id) && (
                      <span className="community-entry-fav" aria-label={copy("community.favorite")}>
                        ★
                      </span>
                    )}
                  </span>
                  <span className="community-entry-stats">
                    <span className="community-stat">{sets === 1 ? copy("community.sets.one", { label: fitConfig.label }) : copy("community.sets.other", { count: sets, label: fitConfig.label })}</span>
                    {prints > 0 && <span className="community-stat is-printed">✓ {prints === 1 ? copy("community.printers.one") : copy("community.printers.other", { count: prints.toLocaleString(locale) })}</span>}
                    {e.downloadCount > 0 && <span className="community-stat">↓ {formatDownloads(copy, e.downloadCount, locale)}</span>}
                  </span>
                  {sibling && !mount && <span className="community-entry-meta">{copy("community.madeFor", { design: titleOf(e.designId) })}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
